// Heuristic: infer which muscle HEADS a training biases, from its name alone.
// Open datasets don't carry head-level muscle data for free (ExerciseDB gated
// it), so we derive it from the movement the way a lifter would read the name.
// Biceps mapping follows the user's reference chart; the rest use standard
// biomechanics (overhead → triceps long head, incline press → upper pec, etc.).
// Returns group-qualified part ids like "biceps:short_head".
//
// ponytail: name-based heuristic, not per-exercise hand data. A rule is a
// smaller, self-correcting surface than 173 tagged rows. Exercises whose
// movement doesn't clearly bias a head stay untagged (empty) on purpose.

// Heads are group-qualified part ids like `${groupId}:${partId}`.
type Rule = Readonly<{ re: RegExp; heads: ReadonlyArray<string>; why: string }>;

// These are EMPHASIS/BIAS flags, not exclusive claims: a curl still trains the
// whole biceps — the head just gets extra work. Rules are guarded (curl rules
// require "curl", press rules exclude curls) after a clinical review flagged
// false positives (e.g. a hammer-grip bench press is not a biceps movement).
// Order matters: earlier rules win their group; a single rule may light more
// than one head of the same group.
const RULES: ReadonlyArray<Rule> = [
  // --- Biceps (emphasis, requires an actual curl). Head bias follows shoulder
  //     position: shoulder EXTENDED (arm behind, incline/drag) pre-stretches the
  //     LONG head; shoulder FLEXED (arm in front, preacher/concentration) biases
  //     the SHORT head. "Inner biceps" names target the (inner) short head. ---
  {
    re: /^(?=.*curl)(?=.*(hammer|reverse|cross.?body|zottman))/i,
    heads: ["biceps:brachialis"],
    why: "neutral/pronated-grip curl → brachialis (+ brachioradialis) emphasis; whole biceps still worked",
  },
  {
    re: /^(?=.*curl)(?=.*(preacher|concentration|spider|prone incline|inner.?bicep))/i,
    heads: ["biceps:short_head"],
    why: "shoulder flexed / arm-in-front (preacher, concentration, inner) → short-head emphasis",
  },
  {
    re: /^(?=.*curl)(?=.*(incline|\bdrag))/i,
    heads: ["biceps:long_head"],
    why: "shoulder extended / arm-behind (incline, drag) → long-head emphasis",
  },

  // --- Triceps. Overhead/skull extensions bias the LONG head; bent-over &
  //     pushdown-style (arm not overhead) bias the LATERAL head. ---
  {
    re: /pushdown|kickback|press.?down|bent.?over.*triceps/i,
    heads: ["triceps:lateral"],
    why: "arm-at-side / bent-over extension → lateral-head emphasis",
  },
  {
    re: /tricep(s)? (extension|press)|skull|french/i,
    heads: ["triceps:long"],
    why: "overhead elbow-extension → long-head emphasis",
  },

  // --- Deltoid (combined & specific movements before the generic ones) ---
  {
    re: /side lateral.*front|front.*side lateral/i,
    heads: ["deltoid:side", "deltoid:front"],
    why: "combined side + front raise → lateral + anterior delt",
  },
  {
    re: /rear (delt|lateral)|reverse fly|reverse flye|face pull/i,
    heads: ["deltoid:rear"],
    why: "horizontal abduction → rear delt",
  },
  {
    re: /scaption/i,
    heads: ["deltoid:front", "deltoid:side"],
    why: "scapular-plane raise → anterior + lateral delt",
  },
  {
    re: /front.*(delt|raise)/i,
    heads: ["deltoid:front"],
    why: "shoulder flexion / front raise → anterior delt",
  },
  {
    re: /lateral raise|side lateral|side raise/i,
    heads: ["deltoid:side"],
    why: "abduction / lateral raise → side delt",
  },
  { re: /upright.*row/i, heads: ["deltoid:side"], why: "upright row → lateral delt" },
  {
    re: /shoulder press|overhead press|arnold|military|push press|(seated|standing).*dumbbell press|see-saw/i,
    heads: ["deltoid:front", "deltoid:side"],
    why: "overhead press → anterior + lateral delt",
  },

  // --- Pecs (bench angle drives the region; not curls) ---
  {
    re: /^(?!.*curl)(?=.*incline)(?=.*(press|bench|fly|flye))/i,
    heads: ["pecs:clav"],
    why: "incline angle → upper (clavicular) pec",
  },
  {
    re: /^(?!.*curl)(?=.*decline)(?=.*(press|bench|fly|flye))/i,
    heads: ["pecs:abd"],
    why: "decline angle → lower sternocostal fibers",
  },
  { re: /chest dip/i, heads: ["pecs:abd"], why: "dip (forward lean) → lower sternocostal fibers" },
  {
    re: /^(?!.*(incline|decline|curl))(?=.*(bench press|dumbbell fly|push-?up))/i,
    heads: ["pecs:stern"],
    why: "flat press/fly → mid (sternocostal) pec",
  },

  // --- Calves ---
  { re: /seated.*calf/i, heads: ["calves:sol"], why: "bent-knee raise → soleus emphasis" },
  {
    re: /calf raise/i,
    heads: ["calves:gm", "calves:gl"],
    why: "straight-leg raise → gastrocnemius emphasis",
  },

  // --- Posterior chain (hip hinge). Biarticular hamstring heads (long head of
  //     biceps femoris, semitendinosus, semimembranosus) drive hip extension;
  //     the short head is knee-only, so hinges bias the biarticular three. ---
  {
    re: /deadlift|rack pull|good morning|back extension|hyperextension/i,
    heads: ["hamstrings:bf_long", "hamstrings:st", "hamstrings:sm", "glutes:max"],
    why: "hip hinge → biarticular hamstrings + glute max",
  },
  {
    re: /hip thrust|glute bridge|glute kickback|hip extension/i,
    heads: ["glutes:max"],
    why: "hip extension against load → glute maximus",
  },
  {
    re: /leg curl|lying.*hamstring|hamstring curl/i,
    heads: ["hamstrings:bf_long", "hamstrings:bf_short", "hamstrings:st", "hamstrings:sm"],
    why: "knee-flexion isolation → whole hamstring incl. short head",
  },

  // --- Knee-dominant (squat pattern). Compound squats/presses/lunges load all
  //     four quads + glute max; leg extension isolates the quads. ---
  {
    re: /leg extension|knee extension/i,
    heads: ["quads:rf", "quads:vl", "quads:vm", "quads:vi"],
    why: "open-chain knee extension → quadriceps isolation",
  },
  {
    re: /squat|leg press|lunge|step-?up|split squat/i,
    heads: ["quads:rf", "quads:vl", "quads:vm", "quads:vi", "glutes:max"],
    why: "knee-dominant compound → quadriceps + glute max",
  },

  // --- Back. Vertical pulls (pulldown/pull-up) bias the lats + teres major;
  //     horizontal rows add the rhomboids (scapular retraction). "Upright row"
  //     is a delt movement, excluded from the row rule. ---
  {
    re: /pull-?up|chin-?up|pulldown|pull down|pullover/i,
    heads: ["back:lats", "back:teres"],
    why: "vertical pull (shoulder adduction) → lats + teres major",
  },
  {
    re: /^(?!.*upright)(?=.*\brow\b)/i,
    heads: ["back:lats", "back:rhomboids", "back:teres"],
    why: "horizontal row (scapular retraction) → lats + rhomboids + teres",
  },

  // --- Traps. Shrugs load the upper (descending) fibers. ---
  {
    re: /shrug/i,
    heads: ["traps:upper"],
    why: "scapular elevation → upper (descending) trapezius",
  },
];

export type HeadInference = Readonly<{ head: string; why: string }>;

// Full trace: which rule fired for each head (for the validation spec).
export const inferExerciseHeadsDetailed = (exerciseName: string): ReadonlyArray<HeadInference> => {
  const claimedGroups = new Set<string>();
  const out: Array<HeadInference> = [];

  for (const rule of RULES) {
    if (!rule.re.test(exerciseName)) continue;

    const group = rule.heads[0]?.split(":")[0] ?? "";
    if (claimedGroups.has(group)) continue;

    claimedGroups.add(group);
    for (const head of rule.heads) out.push({ head, why: rule.why });
  }

  return out;
};

export const inferExerciseHeads = (exerciseName: string): ReadonlyArray<string> =>
  inferExerciseHeadsDetailed(exerciseName).map((h) => h.head);
